/**
 * VideoAdModal — full-screen video ad shown to free-tier users every 20–25
 * swipes on Discover. Users can skip after 5s; watching to completion grants
 * +1 Super Like (server-side via POST /ads/reward-completed).
 *
 * Props:
 *   open (bool)      — controlled visibility
 *   config (object)  — from GET /ads/config
 *                      { video_url, click_url, brand, tagline,
 *                        skip_after_seconds, ad_id }
 *   onClose(rewarded) — called when the modal closes; rewarded=true if user
 *                       watched to completion (super-like already granted).
 *
 * The video is muted by default (browser autoplay policy) with an unmute
 * toggle so users hear the pitch if they want.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Volume2, VolumeX, ExternalLink, Gift, Sparkles } from 'lucide-react';
import { api } from '@/contexts/AuthContext';

export default function VideoAdModal({ open, config, onClose }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rewarding, setRewarding] = useState(false);
  const [rewarded, setRewarded] = useState(false);
  const [wonLabel, setWonLabel] = useState(null); // e.g. "+1 Meet Priority (60 min)"
  // "Tap to unmute" nudge — shows for the first 2s of playback while muted.
  // Once the user unmutes (or manually dismisses), it never re-appears in the same modal open.
  const [showUnmuteNudge, setShowUnmuteNudge] = useState(false);
  // Sep 2026 — video-failure state. Previously the modal rendered a black
  // rectangle when playback couldn't start (codec unsupported, network
  // error, autoplay blocked) with NO feedback to the user. Now we track
  // the failure and render a fallback pane with the sponsor CTA so the
  // user can still convert AND we grant the reward (they still "watched"
  // the slot). Error is a discriminated union: null | 'codec' | 'network' | 'autoplay' | 'unknown'.
  const [videoError, setVideoError] = useState(null);

  const skipAfter = config?.skip_after_seconds ?? 5;
  const canSkip = elapsed >= skipAfter;
  const progressPct = duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0;
  const skipCountdown = Math.max(0, Math.ceil(skipAfter - elapsed));

  // Reset on open so a re-shown modal starts fresh (prevents stale rewarded state).
  useEffect(() => {
    if (open) {
      setElapsed(0);
      setDuration(0);
      setRewarded(false);
      setWonLabel(null);
      setMuted(true);
      setShowUnmuteNudge(true);
      setVideoError(null);
      // Hide the nudge after 2s regardless of interaction.
      const t = setTimeout(() => setShowUnmuteNudge(false), 2000);
      // Play requires an explicit call after DOM mount; use rAF to avoid race.
      // Sep 2026 — surface autoplay-blocked errors so the fallback UI can
      // trigger, instead of silently swallowing into the void.
      requestAnimationFrame(() => {
        try {
          const p = videoRef.current?.play();
          if (p && typeof p.catch === 'function') {
            p.catch((err) => {
              // NotAllowedError = autoplay policy; NotSupportedError = codec.
              // AbortError = user gesture interrupted us; ignore that one.
              if (err?.name === 'NotAllowedError')     setVideoError('autoplay');
              else if (err?.name === 'NotSupportedError') setVideoError('codec');
              else if (err?.name && err.name !== 'AbortError') setVideoError('unknown');
            });
          }
        } catch { /* noop */ }
      });
      return () => clearTimeout(t);
    }
  }, [open]);

  // Sep 2026 · Reward grant when the video fails but the modal was shown.
  // Ad impression happened; user shouldn't be punished with no reward
  // because our media pipeline broke. Fires ONCE per modal-open on error.
  useEffect(() => {
    if (videoError && !rewarded && !rewarding) {
      grantReward();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoError]);

  // Media-element `error` event handler. Runs when the browser can't
  // decode / fetch / demux the file. `<video>.error.code` maps as:
  //   1 = MEDIA_ERR_ABORTED (user aborted)
  //   2 = MEDIA_ERR_NETWORK (network stalled during load)
  //   3 = MEDIA_ERR_DECODE (data corrupted / decoder failed)
  //   4 = MEDIA_ERR_SRC_NOT_SUPPORTED (codec/container not supported)
  const handleVideoError = () => {
    const code = videoRef.current?.error?.code;
    if (code === 2) setVideoError('network');
    else if (code === 3 || code === 4) setVideoError('codec');
    else setVideoError('unknown');
  };

  const grantReward = useCallback(async () => {
    if (rewarded || rewarding) return;
    setRewarding(true);
    try {
      const r = await api.post('/ads/reward-completed', { ad_id: config?.ad_id || '12laws' });
      const label = r.data?.label || '+1 reward';
      setWonLabel(label);
      setRewarded(true);
      toast.success(`Nice — you earned ${label} 💛`, { duration: 4500 });
    } catch (e) {
      // 429 = cooldown (silently swallow — user still finished the video),
      // anything else surface once
      if (e?.response?.status !== 429) {
        toast.error(e?.response?.data?.detail || 'Reward failed');
      }
    } finally {
      setRewarding(false);
    }
  }, [config, rewarded, rewarding]);

  // Fire reward the moment the video ends
  const handleEnded = () => { grantReward(); };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setElapsed(v.currentTime);
    if (!duration && v.duration) setDuration(v.duration);
  };

  const handleClick = () => {
    // Deep-link to sponsor. Analytics: fire a filter-event style ping later if desired.
    window.open(config?.click_url || 'https://the12lawsofdatingdynamics.com/', '_blank', 'noopener');
  };

  const handleSkip = () => {
    if (!canSkip) return;
    onClose?.(false);
  };

  const handleFinishClose = () => {
    onClose?.(rewarded);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
      data-testid="video-ad-modal"
    >
      <div className="relative w-full max-w-md aspect-[9/16] rounded-3xl overflow-hidden bg-black shadow-2xl">
        {/* Video */}
        <video
          ref={videoRef}
          src={config?.video_url || '/ads/12laws_vertical.mp4'}
          className="absolute inset-0 w-full h-full object-cover cursor-pointer"
          playsInline
          autoPlay
          muted={muted}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
          onClick={handleClick}
          onError={handleVideoError}
          data-testid="video-ad-player"
        />

        {/* Sep 2026 · Fallback pane when video can't play. Shows the sponsor
            CTA + a friendly line so the ad slot still converts. Reward
            was already granted above once videoError went truthy. */}
        {videoError && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 bg-gradient-to-b from-[#1a1a1a] via-[#0F0F10] to-[#1a1a1a] text-white text-center"
            data-testid="video-ad-fallback"
          >
            <Sparkles size={44} className="text-[#F5C518]" strokeWidth={1.6} />
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/60 mb-2">Sponsored</p>
              <h3 className="text-[22px] font-black leading-tight mb-2">{config?.brand || 'The 12 Laws of Dating Dynamics'}</h3>
              <p className="text-[13px] leading-relaxed text-white/75 max-w-[280px] mx-auto">{config?.tagline || 'Master the psychology of modern dating'}</p>
            </div>
            <button
              onClick={handleClick}
              className="mt-2 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold text-[#0F0F10] bg-white hover:bg-white/90 transition"
              data-testid="video-ad-fallback-cta"
            >
              Learn more
              <ExternalLink size={13} strokeWidth={2.4} />
            </button>
            <p className="text-[10px] text-white/45 mt-1">Your reward was granted anyway ✓</p>
          </div>
        )}

        {/* Top gradient + Sponsored label */}
        <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/70 bg-white/10 backdrop-blur px-2 py-0.5 rounded-full">
            Sponsored
          </span>
        </div>

        {/* Unmute / mute toggle */}
        <button
          onClick={() => { setMuted((m) => !m); setShowUnmuteNudge(false); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 flex items-center justify-center transition"
          data-testid="video-ad-mute-toggle"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={14} className="text-white" /> : <Volume2 size={14} className="text-white" />}
        </button>

        {/* Tap-to-unmute nudge — first 2s only, while muted */}
        {showUnmuteNudge && muted && (
          <button
            onClick={() => { setMuted(false); setShowUnmuteNudge(false); }}
            data-testid="video-ad-unmute-nudge"
            aria-label="Tap to unmute"
            className="absolute top-14 right-3 flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5 text-[11px] font-[800] text-[#0F0F10] bg-white/95 backdrop-blur shadow-[0_4px_14px_rgba(0,0,0,0.35)] animate-[pulse_1.5s_ease-in-out_infinite]"
            style={{ letterSpacing: '-0.1px' }}
          >
            <Volume2 size={12} strokeWidth={2.4} />
            Tap to unmute
          </button>
        )}

        {/* Progress bar */}
        <div className="absolute bottom-[92px] inset-x-3 h-1 bg-white/15 rounded-full overflow-hidden">
          <div
            className="h-full bg-white/80 transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="video-ad-progress"
          />
        </div>

        {/* Bottom copy + CTAs */}
        <div className="absolute bottom-0 inset-x-0 p-4 pt-8 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#FEF3C7]/80 mb-1">
            The 12 Laws of Dating Dynamics
          </p>
          <h3 className="text-white text-[15px] font-display font-[700] leading-tight mb-3">
            {config?.tagline || 'Master the psychology of modern dating'}
          </h3>

          <div className="flex items-center gap-2">
            {rewarded ? (
              <div className="flex-1 flex items-center gap-1.5 text-[12px] font-bold text-[#B45309] bg-[#FEF3C7] px-3 py-2.5 rounded-full" data-testid="video-ad-rewarded">
                <Sparkles size={13} strokeWidth={2.4} />
                {wonLabel || 'Reward earned'}
              </div>
            ) : (
              <div className="flex-1 flex items-center gap-1 text-[11px] text-white/70">
                <Gift size={11} strokeWidth={2.2} />
                Watch to earn a bonus · Super Like · Boost · swipes
              </div>
            )}
            <button
              onClick={handleClick}
              className="px-3 py-2.5 rounded-full bg-white text-black text-[12px] font-bold inline-flex items-center gap-1.5 hover:bg-white/90 transition"
              data-testid="video-ad-cta"
            >
              Learn more
              <ExternalLink size={11} strokeWidth={2.4} />
            </button>
          </div>
        </div>

        {/* Skip / Close button — top-right below mute, always mounted but greyed until skipAfter */}
        {!rewarded && (
          <button
            onClick={handleSkip}
            disabled={!canSkip}
            className={`absolute top-3 right-14 h-8 px-3 rounded-full text-[11px] font-bold flex items-center gap-1 backdrop-blur transition ${
              canSkip
                ? 'bg-white/95 text-black hover:bg-white'
                : 'bg-white/15 text-white/60 cursor-not-allowed'
            }`}
            data-testid="video-ad-skip"
          >
            {canSkip ? (<>Skip <X size={12} strokeWidth={2.4} /></>) : `Skip in ${skipCountdown}s`}
          </button>
        )}

        {/* When rewarded (video finished), show a Continue button */}
        {rewarded && (
          <button
            onClick={handleFinishClose}
            className="absolute top-3 right-14 h-8 px-3 rounded-full text-[11px] font-bold bg-white text-black flex items-center gap-1 backdrop-blur"
            data-testid="video-ad-continue"
          >
            Continue <X size={12} strokeWidth={2.4} />
          </button>
        )}
      </div>
    </div>
  );
}
